import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Container,
  Tooltip,
  Paper,
  CircularProgress,
  Box,
  TextField,
  Button,
  Modal,
  InputAdornment,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LockIcon from "@mui/icons-material/Lock";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const PeriodTable = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const navigate = useNavigate();
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // State for Info Modal
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") return null;
    try {
      const padded = encodedFormId.padEnd(
        Math.ceil(encodedFormId.length / 4) * 4,
        "="
      );
      const bytes = base64.toByteArray(padded);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  // CORRECTLY PARSE "07:00AM" or "01:00PM" format
  const parseTime12Hour = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") return "";
    const match = timeStr.match(/^(\d{2}):(\d{2})(AM|PM)$/i);
    if (!match) return timeStr; // fallback

    let [_, hours, minutes, period] = match;
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10);
    period = period.toUpperCase();

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    const date = new Date();
    date.setHours(hours);
    date.setMinutes(minutes);

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });

      const result = await response.json();
      const data = result[0];
      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/TimeTable/GetPeriod",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: sessionStorage.getItem("token"),
            },
          }
        );

        if (!response.ok) throw new Error(`Error: ${response.statusText}`);
        const data = await response.json();

        const mappedData = data.map((period) => ({
          id: period.periodId,
          startTime: parseTime12Hour(period.startTime),
          endTime: parseTime12Hour(period.endTime),
          session: `Sequence ${period.sequenceNo}`,
        }));

        setRows(mappedData);
      } catch (err) {
        console.error("Error fetching periods:", err);
        setSnackbarMessage("Failed to load periods");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    Authorizer();
  }, []);

  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      setLoading(true);
      const response = await fetch(
        `https://wsmsv1.aidigitalsec.com/api/TimeTable/DeletePeriod?id=${selectedRow.id}`,
        {
          method: "DELETE",
          headers: { Authorization: sessionStorage.getItem("token") },
        }
      );

      if (!response.ok) throw new Error("Delete failed");

      setRows((prev) => prev.filter((row) => row.id !== selectedRow.id));
      setSnackbarMessage("Period deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (err) {
      setSnackbarMessage("Failed to delete period");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);
      setSelectedRow(null);
    }
  };

  const openDeleteConfirmation = (row) => {
    setSelectedRow(row);
    setOpenDeleteModal(true);
  };

  const filteredRows = rows.filter((row) =>
    row.session.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedData = filteredRows.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);

  // Info sections data for the Info Modal
  const infoSections = [
    {
      icon: <TableChartIcon sx={{ color: "#1976d2", fontSize: 22 }} />,
      title: "Table Columns",
      color: "#e3f2fd",
      borderColor: "#1976d2",
      items: [
        { label: "Start Time", desc: "The time at which the period begins, displayed in 12-hour AM/PM format." },
        { label: "End Time", desc: "The time at which the period ends, displayed in 12-hour AM/PM format." },
        { label: "Period Session", desc: "The session sequence number for the period (e.g. Session 1, Session 2)." },
        { label: "Actions", desc: "Edit or Delete buttons for managing each period record." },
      ],
    },
    {
      icon: <AccessTimeIcon sx={{ color: "#7b1fa2", fontSize: 22 }} />,
      title: "Period Details",
      color: "#f3e5f5",
      borderColor: "#7b1fa2",
      items: [
        { label: "Time Format", desc: "Times are stored in 12-hour format (e.g. 07:00AM, 01:00PM) and displayed with proper AM/PM labels." },
        { label: "Session Number", desc: "Each period is assigned a sequence number that determines its order in the timetable." },
        { label: "Period ID", desc: "Each period has a unique internal ID used for edit and delete operations." },
      ],
    },
    {
      icon: <ManageSearchIcon sx={{ color: "#e65100", fontSize: 22 }} />,
      title: "Search & Pagination",
      color: "#fff3e0",
      borderColor: "#e65100",
      items: [
        { label: "Search", desc: "Filter periods in real time by session name (e.g. type 'Session 1' to find it quickly)." },
        { label: "Rows per Page", desc: "Choose how many records to display per page: 5, 10, 25, or 50." },
        { label: "Prev / Next", desc: "Navigate between pages. Buttons disable automatically at the first and last pages." },
      ],
    },
    {
      icon: <LockIcon sx={{ color: "#2e7d32", fontSize: 22 }} />,
      title: "Actions & Permissions",
      color: "#e8f5e9",
      borderColor: "#2e7d32",
      items: [
        { label: "ADD PERIOD", desc: "Opens the Add Period form to create a new timetable period. Requires Write (uWrite) permission." },
        { label: "Edit", desc: "Opens the Edit Period form pre-filled with the selected period's data. Requires Modify (uModify) permission." },
        { label: "Delete", desc: "Prompts a confirmation dialog before permanently removing the period. Requires Delete (uDelete) permission." },
        { label: "Unauthorized", desc: "Hovering over a disabled button shows a tooltip explaining you are not authorized for that action." },
      ],
    },
  ];

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          boxShadow: 3,
          borderRadius: "4px 4px 0 0",
          margin: "2px auto",
          display: "flex",
          alignItems: "center",
          justifyContent: 'flex-start',
gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Time Period
        </Typography>

        {/* Info Button */}
        <Tooltip title="View page information & guide" arrow placement="left">
          <IconButton
            onClick={() => setInfoModalOpen(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              backgroundColor: "rgba(255,255,255,0.1)",
              border: "1.5px solid rgba(255,255,255,0.25)",
              borderRadius: "50%",
              width: 32,
              height: 32,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#1976d2",
                borderColor: "#64B5F6",
                color: "#ffffff",
                boxShadow: "0 0 0 3px rgba(100,181,246,0.25)",
                transform: "scale(1.1)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <TextField
            variant="outlined"
            placeholder="Search Session..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300, backgroundColor: "#f9f9f9" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={!canAdd ? "Not authorized to add" : ""} arrow>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/addperiodseq")}
                disabled={!canAdd}
                sx={{ height: 50, fontWeight: "bold", px: 3 }}
              >
                ADD PERIOD
              </Button>
            </span>
          </Tooltip>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650, border: "1px solid #ddd" }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Start Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    borderRight: "1px solid #ddd",
                  }}
                >
                  End Time
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Sequence
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No Periods Found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                      {row.startTime}
                    </TableCell>
                    <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                      {row.endTime}
                    </TableCell>
                    <TableCell sx={{ borderRight: "1px solid #ddd" }}>
                      {row.session}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Tooltip title={!canEdit ? "Not authorized" : ""}>
                        <span>
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() =>
                              navigate(`/edit-period/${encodedFormId}`, {
                                state: { id: row.id },
                              })
                            }
                            disabled={!canEdit}
                            sx={{ mr: 1 }}
                          >
                            Edit
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={!canDelete ? "Not authorized" : ""}>
                        <span>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => openDeleteConfirmation(row)}
                            disabled={!canDelete}
                          >
                            Delete
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(+e.target.value);
                setPage(1);
              }}
              SelectProps={{ native: true }}
              sx={{ width: 120 }}
            >
              {rowsPerPageOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ mr: 2, display: "inline" }}>
                Page {page} of {totalPages}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box sx={{ bgcolor: "#0B1F40", p: 2, mb: 2, borderRadius: 1 }}>
            <Typography variant="h6" color="white">
              Delete Confirmation
            </Typography>
          </Box>
          <Typography sx={{ mb: 3 }}>
            Are you sure you want to delete this period?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setOpenDeleteModal(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error">
              Yes, Delete
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: 620, md: 680 },
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(11,31,61,0.22)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0B1F40 0%, #1565C0 100%)",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  id="info-modal-title"
                  variant="h6"
                  sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}
                >
                  Time Period — Guide
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.65)" }}
                >
                  Complete reference for all features and fields
                </Typography>
              </Box>
            </Box>
           
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box
            sx={{
              overflowY: "auto",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Page Overview */}
            <Box
              sx={{
                backgroundColor: "#f0f4ff",
                border: "1px solid #c5d3f0",
                borderRadius: 2,
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#1a237e", lineHeight: 1.7 }}
              >
                The <strong>Time Period</strong> page manages timetable periods
                for your institution. Each period defines a start time, end
                time, and session sequence number used when building class
                timetables.
              </Typography>
            </Box>

            {/* Sections */}
            {infoSections.map((section, sIdx) => (
              <Box
                key={sIdx}
                sx={{
                  backgroundColor: section.color,
                  border: `1px solid ${section.borderColor}30`,
                  borderLeft: `4px solid ${section.borderColor}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
                >
                  {section.icon}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a2e",
                      fontSize: "0.95rem",
                    }}
                  >
                    {section.title}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}
                >
                  {section.items.map((item, iIdx) => (
                    <Box
                      key={iIdx}
                      sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                    >
                      <Chip
                        label={item.label}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 22,
                          flexShrink: 0,
                          backgroundColor: `${section.borderColor}18`,
                          color: section.borderColor,
                          border: `1px solid ${section.borderColor}40`,
                          mt: "1px",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#424242",
                          lineHeight: 1.5,
                          fontSize: "0.82rem",
                        }}
                      >
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Tip Box */}
            <Box
              sx={{
                backgroundColor: "#fffde7",
                border: "1px solid #f9a825",
                borderRadius: 2,
                p: 2,
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Typography sx={{ fontSize: "1.1rem", mt: "1px" }}>💡</Typography>
              <Typography
                variant="body2"
                sx={{ color: "#5d4037", lineHeight: 1.65, fontSize: "0.82rem" }}
              >
                <strong>Pro Tip:</strong> Periods are ordered by their session
                sequence number. Make sure to define periods in the correct
                order so your timetable slots align properly throughout the
                school day.
              </Typography>
            </Box>
          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",
              px: 2.5,
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              flexShrink: 0,
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              onClick={() => setInfoModalOpen(false)}
              variant="contained"
              color="primary"
              sx={{ fontWeight: 600, px: 3 }}
            >
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PeriodTable;